// sync-decision-logic.ts — pure decision functions for the cloud-sync hook
// (use-cloud-sync.ts). No React, no I/O — tách riêng để các quyết định
// push/pull/echo unit-test được mà không cần mock Supabase (F2).

import { hasPendingSyncItems } from "./lifeOsMutations";
import { INDEXED_STATE_PLACEHOLDER_UPDATED_AT } from "./storage";
import type { LifeOsState } from "../types";

export type RemoteSnapshotAction = "first-push" | "push-pending" | "skip-echo" | "merge";

/**
 * Quyết định phản ứng với một remote snapshot vừa emit:
 * - "first-push":   cloud trống (user mới) → đẩy toàn bộ local lên làm bản gốc.
 * - "push-pending": remote cũ hơn local VÀ còn queue → đẩy pending thay vì pull.
 * - "skip-echo":    echo của chính push gần nhất, không còn gì pending → bỏ qua
 *                   (không merge, không stringify).
 * - "merge":        còn lại → merge LWW per-item rồi adopt nếu nội dung đổi.
 */
export function decideRemoteSnapshotAction(input: {
  remote: LifeOsState | null;
  local: LifeOsState;
  /** state.updatedAt của lần push/adopt thành công gần nhất — "" khi chưa có. */
  lastSeenRemoteUpdatedAt: string;
}): RemoteSnapshotAction {
  const { remote, local, lastSeenRemoteUpdatedAt } = input;
  if (!remote) {
    return "first-push";
  }
  if (
    new Date(remote.updatedAt).getTime() < new Date(local.updatedAt).getTime() &&
    hasPendingSyncItems(local)
  ) {
    return "push-pending";
  }
  // Guard hasPendingSyncItems: queue pending có thể che khác biệt remote —
  // skip sớm sẽ chặn merge/re-push flush queue, nên vẫn phải merge.
  if (remote.updatedAt === lastSeenRemoteUpdatedAt && !hasPendingSyncItems(local)) {
    return "skip-echo";
  }
  return "merge";
}

/**
 * So sánh 2 snapshot: updatedAt trước (rẻ, khác là kết luận ngay);
 * JSON.stringify chỉ chạy khi updatedAt bằng nhau (F3 — tránh stringify
 * full state trên đường nóng).
 */
export function isSameStateSnapshot(a: LifeOsState, b: LifeOsState): boolean {
  if (a === b) return true;
  if (a.updatedAt !== b.updatedAt) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Gate rẻ cho debounced push (F3): chỉ schedule khi có queue pending —
 * mọi mutation đều đi qua markDirty/markDeleted nên queue là tín hiệu đủ.
 * Placeholder IndexedDB (chưa hydrate xong) không bao giờ được push.
 */
export function shouldSchedulePush(state: LifeOsState): boolean {
  if (state.updatedAt === INDEXED_STATE_PLACEHOLDER_UPDATED_AT) return false;
  return hasPendingSyncItems(state);
}
